import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, RootState } from "../../../store/store";
import BASE_URL from "../api";
import { Property } from "../types/property";


type PropertyFilters = {
  property_type?: string;
  listing_type?: string;
  min_price?: number;
  max_price?: number;
  bedrooms?: number;
  city?: string;
};

interface PropertyState {
  properties: Property[];
  property: Property | null;

  loading: boolean;
  error: any;

  filters: PropertyFilters;

  pagination: {
    page: number;
    per_page: number;
    total?: number;
    last_page?: number;
  };
  assignments: any[];
  loadingAssignments: boolean;

  managementProperty: any | null;
  loadingManagementProperty: boolean;

}

const initialState: PropertyState = {
  properties: [],
  property: null,
  loading: false,
  error: null,
  filters: {},
  pagination: {
    page: 1,
    per_page: 10,
  },
  assignments: [],
  loadingAssignments: false,

  managementProperty: null,
  loadingManagementProperty: false,
};

/* =======================
   API HELPER
======================= */

async function api<T>(
  path: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  token?: string,
  body?: unknown
): Promise<T> {
  const isFormData = body instanceof FormData;

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers: {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...(token && { Authorization: `Bearer ${token}` }),
      Accept: "application/json",
    },
    body: body
      ? isFormData
        ? (body as FormData)
        : JSON.stringify(body)
      : undefined,
  });

  const data = await res.json();

  if (!res.ok) {
    const message =
      data?.error || //  PRIMARY (your Laravel format)
      data?.message ||
      (data?.errors && Object.values(data.errors).flat()?.[0]) ||
      "Request failed";

    throw new Error(message);
  }

  return data as T;
}

const getAuthToken = (state: RootState, role: "user" | "admin") => {
  return role === "admin"
    ? state.admin.access_token
    : state.auth.access_token;
};

const propertySlice = createSlice({
  name: "property",
  initialState,
  reducers: {
    start(state) {
      state.loading = true;
      state.error = null;
      // state.property = null;
    },

    fail(state, action: PayloadAction<any>) {
      state.loading = false;
      state.error = action.payload;
    },

    fetchPropertiesSuccess(state, action: PayloadAction<Property[]>) {
      state.loading = false;
      state.properties = action.payload;
      // state.properties = action.payload;
    },

    fetchPropertySuccess(state, action: PayloadAction<Property>) {
      state.loading = false;
      state.property = action.payload;
    },

    setFilters(state, action: PayloadAction<PropertyState["filters"]>) {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },

    setPage(state, action: PayloadAction<number>) {
      state.pagination.page = action.payload;
    },

    setPagination(state, action: PayloadAction<any>) {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },

    createPropertySuccess(state, action: PayloadAction<Property>) {
      state.loading = false;
      state.properties.unshift(action.payload);
    },

    // updatePropertySuccess(state, action: PayloadAction<Property>) {
    //   state.loading = false;

    //   const index = state.properties.findIndex(p => p.id === action.payload.id);
    //   if (index !== -1) state.properties[index] = action.payload;
    // },

    updatePropertySuccess(state, action: PayloadAction<Property>) {
      state.loading = false;

      // update list
      const index = state.properties.findIndex(
        (p) => p.id === action.payload.id
      );

      if (index !== -1) {
        state.properties[index] = action.payload;
      }

      // update single property
      state.property = action.payload;
    },

    deletePropertySuccess(state, action: PayloadAction<string>) {
      state.loading = false;
      state.properties = state.properties.filter(
        p => p.id !== action.payload
      );
    },


    // USER
    myPropertiesSuccess(state, action: PayloadAction<Property[]>) {
      state.loading = false;
      state.properties = action.payload;
    },

    myPropertyDetailsSuccess(state, action: PayloadAction<Property>) {
      state.loading = false;
      state.property = action.payload;
    },
    resetProperties: (state) => {
      state.properties = [];
      state.property = null;
      state.loading = false;
    },

    // PROPERTY ASSIGNMENT

    setAssignments(state, action: PayloadAction<any[]>) {
      state.assignments = action.payload;
    },

    setAssignmentsLoading(state, action: PayloadAction<boolean>) {
      state.loadingAssignments = action.payload;
    },

    addAssignment(state, action: PayloadAction<any>) {
      state.assignments.unshift(action.payload);
    },

    removeAssignmentSuccess(state, action: PayloadAction<string>) {
      state.assignments = state.assignments.filter(
        (a) => a.id !== action.payload
      );
    },

    setManagementProperty(state, action: PayloadAction<any>) {
      state.managementProperty = action.payload;
      state.loading = false;
    },

    setManagementLoading(state, action: PayloadAction<boolean>) {
      state.loadingManagementProperty = action.payload;
    },

    userManagementPropertiesSuccess(
      state,
      action: PayloadAction<Property[]>
    ) {
      state.loading = false;
      state.properties = action.payload;
    },

    userManagementPropertyDetailsSuccess(
      state,
      action: PayloadAction<Property>
    ) {
      state.loading = false;
      state.property = action.payload;
    },

  },
});

export const {
  start,
  fail,
  fetchPropertiesSuccess,
  fetchPropertySuccess,
  setFilters,
  setPagination,
  createPropertySuccess,
  updatePropertySuccess,
  deletePropertySuccess,
  myPropertiesSuccess,
  myPropertyDetailsSuccess,
  resetProperties,
  setAssignments,
  setAssignmentsLoading,
  addAssignment,
  removeAssignmentSuccess,
  userManagementPropertiesSuccess,
  userManagementPropertyDetailsSuccess,
} = propertySlice.actions;

export default propertySlice.reducer;

export const fetchProperties =
  () => async (dispatch: AppDispatch) => {
    dispatch(start());

    try {
      const res = await fetch(`${BASE_URL}/properties`);
      const data = await res.json();

      const raw = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : [];

      const properties = raw.map((p: any) => ({
        ...p,
        images:
          typeof p.images === "string"
            ? JSON.parse(p.images)
            : p.images || [],

        average_rating: p.comments_avg_rating ?? p.average_rating ?? 0,
      }));

      dispatch(fetchPropertiesSuccess(properties));
      // console.log("FETCHED PROPERTIES...", properties);
    } catch (err) {
      dispatch(fail(err));
    }
  };


export const fetchProperty =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(start());

    try {
      const res = await fetch(`${BASE_URL}/properties/${id}`);
      const data = await res.json();

      const p = data?.data ?? data;

      if (!p) throw new Error("No property returned");

      const property = {
        ...p,

        images:
          typeof p.images === "string"
            ? JSON.parse(p.images)
            : Array.isArray(p.images)
              ? p.images
              : [],

        assignments: Array.isArray(p.assignments)
          ? p.assignments.map((a: any) => ({
            id: a.id,
            role: a.role ?? a.assigned_role ?? null,
            //role: a.role,

            user: a.user
              ? {
                id: a.user.id,
                first_name: a.user.first_name,
                last_name: a.user.last_name,
                email: a.user.email,
                image: a.user.image,
              }
              : null,
          }))
          : [],

        // ✅ SAFE COMMENTS
        comments: Array.isArray(p.comments)
          ? p.comments.map((c: any) => ({
            ...c,
            user: c.user
              ? {
                id: c.user.id,
                // name: c.user.name,
                name: c.user
                  ? `${c.user.first_name} ${c.user.last_name}`
                  : "Unknown",
                image: c.user.image,
              }
              : null,
          }))
          : [],

        // ✅ SAFE RATING
        average_rating:
          p.comments_avg_rating ??
          p.average_rating ??
          0,
      };

      dispatch(fetchPropertySuccess(property));
    } catch (err) {
      dispatch(fail(err));
    }
  };


// ADMIN FETCH PROPERTY DETAILS
export const adminFetchProperty =
  (id: string) => async (dispatch: AppDispatch) => {
    dispatch(start());

    try {
      const res = await fetch(`${BASE_URL}/properties/${id}`);
      const data = await res.json();

      const property = data?.data;

      dispatch(fetchPropertySuccess({
        ...property,

        images:
          typeof property.images === "string"
            ? JSON.parse(property.images)
            : property.images || [],

        // ✅ normalize rating
        average_rating:
          property.comments_avg_rating ?? property.average_rating ?? 0,

        // ✅ ensure comments exist
        comments: property.comments || [],
      }));

    } catch (err) {
      dispatch(fail(err));
    }
  };


export const createProperty =
  (body: FormData) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;



        const res = await fetch(`${BASE_URL}/user/properties`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
          body,
        });

        const data = await res.json();




        if (!res.ok) throw data;

        dispatch(createPropertySuccess(data.data));
        //dispatch(fetchProperties());
      } catch (err: any) {

        dispatch(fail(err.message || err));
        throw err;
      }
    };

export const updateProperty =
  (id: string, body: FormData) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;

        const res = await fetch(`${BASE_URL}/user/properties/${id}`, {
          method: "PUT", // Laravel file upload
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body,
        });

        const data = await res.json();
        if (!res.ok) throw data;

        dispatch(updatePropertySuccess(data.data));
      } catch (err) {
        dispatch(fail(err));
      }
    };

export const deleteProperty =
  (id: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getState().auth.access_token;

        await fetch(`${BASE_URL}/user/properties/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        //dispatch(deletePropertySuccess(Number(id)));
        dispatch(deletePropertySuccess(id));
      } catch (err) {
        dispatch(fail(err));
      }
    };


//USER PROPERTY

export const fetchMyProperties =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(start());

    try {
      const token = getAuthToken(getState(), "user");

      const res = await fetch(`${BASE_URL}/user/my-properties`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = await res.json();

      const raw = data?.data || [];

      const properties = raw.map((p: any) => ({
        ...p,

        images: Array.isArray(p.image_urls)
          ? p.image_urls
          : Array.isArray(p.images)
            ? p.images.map((img: string) =>
              img.startsWith("http")
                ? img
                : `${BASE_URL}/${img}`
            )
            : [],

        average_rating: p.comments_avg_rating ?? p.average_rating ?? 0,
        comments_count: p.comments_count ?? 0,
        comments: Array.isArray(p.comments) ? p.comments : [],
      }));



      dispatch(myPropertiesSuccess(properties));
    } catch (err) {
      dispatch(fail(err));
    }
  };

export const fetchMyPropertyDetails =
  (id: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        const res = await fetch(
          `${BASE_URL}/user/my-properties-details/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const data = await res.json();

        const p = data?.data;

        if (!p) throw new Error("Property not found");

        const property = {
          ...p,

          // ✅ images normalization (VERY IMPORTANT)
          images:
            typeof p.images === "string"
              ? JSON.parse(p.images)
              : p.images || [],

          image_urls: p.image_urls || [],

          assignments: Array.isArray(p.assignments)
            ? p.assignments.map((a: any) => ({
              id: a.id,
              role: a.role ?? a.assigned_role ?? null,
              //role: a.role,

              user: a.user
                ? {
                  id: a.user.id,
                  first_name: a.user.first_name,
                  last_name: a.user.last_name,
                  email: a.user.email,
                  image: a.user.image,
                }
                : null,
            }))
            : [],

          // ✅ comments normalization
          comments: Array.isArray(p.comments)
            ? p.comments.map((c: any) => ({
              id: c.id,
              rating: Number(c.rating) || 0,
              comment: c.comment,
              created_at: c.created_at,

              user: c.user
                ? {
                  id: c.user.id,
                  name: `${c.user.first_name} ${c.user.last_name}`,
                  image: c.user.image,
                }
                : null,
            }))
            : [],

          // ✅ rating safety
          average_rating:
            p.comments_avg_rating ?? p.average_rating ?? 0,

          // ✅ count safety
          comments_count: p.comments_count ?? 0,
        };

        dispatch(myPropertyDetailsSuccess(property));
      } catch (err) {
        dispatch(fail(err));
      }
    };


// PROPERTY ASSIGNMENT


export const fetchPropertyAssignments =
  (propertyId: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        const res = await fetch(
          `${BASE_URL}/user/properties/${propertyId}/assignments`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const data = await res.json();

        if (!res.ok) throw data;

        // optional: store inside property object
        dispatch(
          fetchPropertySuccess({
            ...(getState().property.property as any),
            assignments: data.data,
          })
        );

      } catch (err: any) {
        dispatch(fail(err?.message || err));
      }
    };

export const assignUserToProperty =
  (propertyId: string, user_id: string, role: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        const res = await fetch(
          `${BASE_URL}/user/properties/${propertyId}/assign`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              user_id,
              role, // ✅ IMPORTANT
            }),
          }
        );

        const data = await res.json();

        // if (!res.ok) throw data;
        if (!res.ok) {
          console.error("Assign error:", data);
          throw new Error(data?.message || "Assignment failed");
        }

        dispatch(fetchMyPropertyDetails(propertyId));

      } catch (err: any) {
        console.error("Assign error:", err);

        const message =
          err?.message ||
          err?.error ||
          JSON.stringify(err) ||
          "Assignment failed";

        dispatch(fail(message));
        throw new Error(message);
      }
    };

export const removePropertyAssignment =
  (propertyId: string, userId: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        const res = await fetch(
          `${BASE_URL}/properties/${propertyId}/assign/${userId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const data = await res.json();

        if (!res.ok) throw data;

        // refresh property after delete
        dispatch(fetchProperty(propertyId));

      } catch (err: any) {
        dispatch(fail(err?.message || err));
        throw err;
      }
    };

/////////////////////////////
export const fetchUserManagementProperties =
  () => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(start());

    try {
      const token = getAuthToken(getState(), "user");

      const res = await fetch(`${BASE_URL}/user/user-property-management`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = await res.json();

      if (!res.ok) throw data;

      const raw = Array.isArray(data?.data) ? data.data : [];

      const properties = raw.map((p: any) => ({
        ...p,

        // images normalization
        images: Array.isArray(p.images)
          ? p.images
          : typeof p.images === "string"
            ? JSON.parse(p.images || "[]")
            : [],

        image_urls: Array.isArray(p.image_urls)
          ? p.image_urls
          : [],

        // rating safety
        average_rating: p.comments_avg_rating ?? p.average_rating ?? 0,

        // IMPORTANT FIX: normalize assignments
        assignments: Array.isArray(p.assignments)
          ? p.assignments.map((a: any) => ({
            id: a.id,
            role: a.assigned_role ?? a.role ?? null,

            user: a.user
              ? {
                id: a.user.id,
                first_name: a.user.first_name,
                last_name: a.user.last_name,
                email: a.user.email,
                image: a.user.image,
              }
              : null,
          }))
          : [],
      }));

      dispatch(fetchPropertiesSuccess(properties));
    } catch (err: any) {
      dispatch(fail(err?.message || err));
    }
  };

export const fetchUserManagementPropertyDetails =
  (id: string) =>
    async (dispatch: AppDispatch, getState: () => RootState) => {
      dispatch(start());

      try {
        const token = getAuthToken(getState(), "user");

        const res = await fetch(
          `${BASE_URL}/user/user-property-management/${id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const data = await res.json();

        if (!res.ok) throw data;

        const p = data?.data;

        const property = {
          ...p,

          image_urls: Array.isArray(p.image_urls)
            ? p.image_urls
            : [],

          images: Array.isArray(p.images)
            ? p.images
            : typeof p.images === "string"
              ? JSON.parse(p.images || "[]")
              : [],

          average_rating: p.comments_avg_rating ?? 0,

          assignments: Array.isArray(p.assignments)
            ? p.assignments.map((a: any) => ({
              id: a.id,
              role: a.assigned_role ?? a.role,

              property: a.property ?? null,

              user: a.user
                ? {
                  id: a.user.id,
                  first_name: a.user.first_name,
                  last_name: a.user.last_name,
                  email: a.user.email,
                  image: a.user.image,
                }
                : null,
            }))
            : [],
        };

        dispatch({
          type: "property/setManagementProperty",
          payload: property,
        });
      } catch (err: any) {
        dispatch(fail(err?.message || err));
      }
    };



export type { PropertyState };